import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

// --- Type Definitions ---
type SliceStyle = {
  backgroundColor: string;
  textColor: string;
};

type Slice = {
  id: number; // ID from DB
  label: string;
  value: string;
  probability: number;
  style: SliceStyle;
};

type WheelSettings = {
  slices: Slice[];
  wheelTitle: string;
  wheelSubtitle: string;
  buttonText: string;
  buttonBackground: string;
  buttonTextColor: string;
  backgroundColor: string;
  panelTextColor: string;
  markerColor: string;
  outerDotColor: string;
  centerImage: string;
  cooldownEnabled: boolean;
};

type ThemeField =
  | 'wheelSubtitle'
  | 'buttonBackground'
  | 'buttonTextColor'
  | 'backgroundColor'
  | 'panelTextColor'
  | 'markerColor'
  | 'outerDotColor'
  | 'centerImage';

const APPEARANCE_DEFAULTS = {
  wheelSubtitle: 'İndirim Yakala',
  buttonBackground: '#FFFFFF',
  buttonTextColor: '#0D0D0D',
  backgroundColor: '#060606',
  panelTextColor: '#FFFFFF',
  markerColor: '#FF3B30',
  outerDotColor: '#FFCC00',
  centerImage: '',
};

function AdminPage() {
  const [settings, setSettings] = useState<WheelSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  // Fetch initial config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/config`);
        if (!response.ok) throw new Error('Network response was not ok');
        const data: WheelSettings = await response.json();
        const normalized: WheelSettings = {
          ...data,
          wheelSubtitle: data.wheelSubtitle || APPEARANCE_DEFAULTS.wheelSubtitle,
          buttonBackground: data.buttonBackground || APPEARANCE_DEFAULTS.buttonBackground,
          buttonTextColor: data.buttonTextColor || APPEARANCE_DEFAULTS.buttonTextColor,
          backgroundColor: data.backgroundColor || APPEARANCE_DEFAULTS.backgroundColor,
          panelTextColor: data.panelTextColor || APPEARANCE_DEFAULTS.panelTextColor,
          markerColor: data.markerColor || APPEARANCE_DEFAULTS.markerColor,
          outerDotColor: data.outerDotColor || APPEARANCE_DEFAULTS.outerDotColor,
          centerImage: data.centerImage || APPEARANCE_DEFAULTS.centerImage,
          slices: data.slices || [],
          cooldownEnabled: typeof data.cooldownEnabled === 'boolean' ? data.cooldownEnabled : true,
        };
        setSettings(normalized);
      } catch (e) {
        setError('Failed to load wheel configuration.');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSliceChange = (index: number, field: keyof Slice | keyof SliceStyle, value: string | number) => {
    if (!settings) return;
    const newSlices = [...settings.slices];
    const sliceToUpdate = { ...newSlices[index] };

    if (field === 'backgroundColor' || field === 'textColor') {
      sliceToUpdate.style = { ...sliceToUpdate.style, [field]: value };
    } else if (field === 'probability') {
        sliceToUpdate[field] = Number(value);
    } else if (field === 'label' || field === 'value') {
        sliceToUpdate[field] = String(value);
    }

    newSlices[index] = sliceToUpdate;
    setSettings({ ...settings, slices: newSlices });
  };

  const addSlice = () => {
    if (!settings) return;
    const newSlice: Slice = {
      id: Date.now(), // Temporary ID
      label: 'Yeni Ödül',
      value: `NEW_${Date.now()}`,
      probability: 0,
      style: { backgroundColor: '#cccccc', textColor: '#000000' },
    };
    setSettings({ ...settings, slices: [...settings.slices, newSlice] });
  };

  const removeSlice = (index: number) => {
    if (!settings) return;
    const newSlices = settings.slices.filter((_, i) => i !== index);
    setSettings({ ...settings, slices: newSlices });
  };

  const handleThemeChange = (field: ThemeField, value: string) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
  };

  const toggleCooldown = () => {
    if (!settings) return;
    setSettings({ ...settings, cooldownEnabled: !settings.cooldownEnabled });
  };

  const handleImageUpload = (file?: File | null) => {
    if (!file || !settings) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      handleThemeChange('centerImage', reader.result?.toString() || '');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    if (!settings) return;
    setSaving(true);
    setError(null);

    // Validate probabilities
    const totalProb = settings.slices.reduce((sum, s) => sum + s.probability, 0);
    if (totalProb < 99.9 || totalProb > 100.1) {
      setError(`Olasılıkların toplamı 100 olmalıdır. Mevcut toplam: ${totalProb.toFixed(2)}`);
      setSaving(false);
      return;
    }

    try {
      // We will use the simplified endpoint that replaces all slices
      const sliceResponse = await fetch(`${API_BASE_URL}/admin/slices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings.slices),
      });
      if (!sliceResponse.ok) {
        const errData = await sliceResponse.json();
        throw new Error(errData.error || 'Failed to save slices');
      }

      const settingsPayload = {
        wheelTitle: settings.wheelTitle,
        wheelSubtitle: settings.wheelSubtitle,
        buttonText: settings.buttonText,
        buttonBackground: settings.buttonBackground,
        buttonTextColor: settings.buttonTextColor,
        backgroundColor: settings.backgroundColor,
        panelTextColor: settings.panelTextColor,
        markerColor: settings.markerColor,
        outerDotColor: settings.outerDotColor,
        centerImage: settings.centerImage,
        cooldownEnabled: settings.cooldownEnabled,
      };

      const settingsResponse = await fetch(`${API_BASE_URL}/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsPayload),
      });
      if (!settingsResponse.ok) {
        const errData = await settingsResponse.json();
        throw new Error(errData.error || 'Failed to save settings');
      }

      alert('Ayarlar başarıyla kaydedildi!');

    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!settings) return <div>{error || 'No settings found.'}</div>;

  const totalProbability = settings.slices.reduce((sum, s) => sum + s.probability, 0);
  const colorControls: Array<{ key: Exclude<ThemeField, 'centerImage' | 'wheelSubtitle'>; label: string; helper?: string }> = [
    { key: 'backgroundColor', label: 'Popup Arka Planı', helper: 'Çark sayfasının ana arka plan rengi.' },
    { key: 'panelTextColor', label: 'Metin Rengi', helper: 'Başlık ve açıklamalar için.' },
    { key: 'buttonBackground', label: 'Buton Arka Planı' },
    { key: 'buttonTextColor', label: 'Buton Yazı Rengi' },
    { key: 'markerColor', label: 'Gösterge (Ok) Rengi' },
    { key: 'outerDotColor', label: 'Çevre Noktaları' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <p className="text-sm uppercase tracking-[0.4em] text-slate-500">Çark Tasarımı</p>
            <h1 className="mt-1 text-3xl font-bold text-slate-900">Yönetim Paneli</h1>
            <p className="text-sm text-slate-500">Tüm ayarları düzenleyip tek tuşla kaydedin.</p>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Genel Ayarlar */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Genel Metinler</h2>
              <span className="text-xs uppercase tracking-[0.3em] text-slate-400">Kopya</span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600">Çark Başlığı</label>
                <input
                  type="text"
                  value={settings.wheelTitle}
                  onChange={(e) => setSettings({ ...settings, wheelTitle: e.target.value })}
                  className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Alt Başlık</label>
                <input
                  type="text"
                  value={settings.wheelSubtitle}
                  onChange={(e) => handleThemeChange('wheelSubtitle', e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600">Buton Metni</label>
                <input
                  type="text"
                  value={settings.buttonText}
                  onChange={(e) => setSettings({ ...settings, buttonText: e.target.value })}
                  className="mt-1 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                />
              </div>
              <div className="flex flex-col gap-2 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Test Modu</p>
                  <p className="text-xs text-slate-500">24 saatlik bekleme süresini aç/kapat. Kapalıyken dilediğiniz kadar deneme yapabilirsiniz.</p>
                </div>
                <label className="inline-flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-600">{settings.cooldownEnabled ? 'Aktif' : 'Pasif'}</span>
                  <button
                    type="button"
                    onClick={toggleCooldown}
                    className={`relative h-6 w-12 rounded-full transition ${
                      settings.cooldownEnabled ? 'bg-emerald-500' : 'bg-slate-400'
                    }`}
                  >
                    <span
                      className={`absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-white shadow transition ${
                        settings.cooldownEnabled ? 'left-6' : 'left-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
            </div>
          </div>

          {/* Tema */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Tema & Renkler</h2>
              <span className="text-xs uppercase tracking-[0.3em] text-slate-400">Stil</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {colorControls.map(({ key, label, helper }) => (
                <div key={key} className="rounded-2xl border border-slate-100 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700">{label}</p>
                      {helper && <p className="text-xs text-slate-400">{helper}</p>}
                    </div>
                    <span
                      className="inline-flex h-8 w-8 rounded-full border border-slate-200 shadow-inner"
                      style={{ backgroundColor: settings[key] as string }}
                    />
                  </div>
                  <input
                    type="color"
                    value={settings[key] as string}
                    onChange={(e) => handleThemeChange(key, e.target.value)}
                    className="mt-3 block h-12 w-full cursor-pointer rounded-xl border border-slate-200 bg-white"
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-slate-50/70 p-4">
              <p className="text-sm font-semibold text-slate-700">Merkez Görseli</p>
              <p className="text-xs text-slate-500">Logonuzu veya markanıza ait bir ikonu yükleyebilirsiniz.</p>
              {settings.centerImage ? (
                <div className="mt-4 flex items-center gap-4">
                  <img
                    src={settings.centerImage}
                    alt="Merkez görseli"
                    className="h-20 w-20 rounded-full border border-slate-200 object-cover shadow-inner"
                  />
                  <button
                    type="button"
                    onClick={() => handleThemeChange('centerImage', '')}
                    className="text-sm font-medium text-red-500 hover:text-red-600"
                  >
                    Görseli Kaldır
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-sm text-slate-500">Henüz bir görsel yüklenmedi.</p>
              )}
              <div className="mt-4 flex flex-wrap gap-3">
                <label className="inline-flex cursor-pointer items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-500">
                  Görsel Yükle
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => handleImageUpload(event.target.files?.[0])}
                  />
                </label>
                {settings.centerImage && (
                  <button
                    type="button"
                    onClick={() => handleThemeChange('centerImage', '')}
                    className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:border-slate-400"
                  >
                    Temizle
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Slices */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Dilimler (Ödüller)</h2>
              <p className="text-sm text-slate-500">Her dilim için oranları ve renkleri belirleyin.</p>
            </div>
            <div className={`text-sm font-semibold ${totalProbability.toFixed(2) === '100.00' ? 'text-green-600' : 'text-red-500'}`}>
              Toplam Olasılık: {totalProbability.toFixed(2)}%
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="hidden grid-cols-12 gap-3 px-4 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid">
              <span className="col-span-3">Dilim İsmi</span>
              <span className="col-span-3">Değer</span>
              <span className="col-span-2">Olasılık (%)</span>
              <span className="col-span-2">Arka Plan Rengi</span>
              <span className="col-span-2">Yazı Rengi</span>
            </div>
            {settings.slices.map((slice, index) => (
              <div key={slice.id} className="grid grid-cols-12 gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <input
                  type="text"
                  placeholder="Etiket"
                  value={slice.label}
                  onChange={(e) => handleSliceChange(index, 'label', e.target.value)}
                  className="col-span-12 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 md:col-span-3"
                />
                <input
                  type="text"
                  placeholder="Değer"
                  value={slice.value}
                  onChange={(e) => handleSliceChange(index, 'value', e.target.value)}
                  className="col-span-12 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 md:col-span-3"
                />
                <input
                  type="number"
                  placeholder="Olasılık (%)"
                  value={slice.probability}
                  onChange={(e) => handleSliceChange(index, 'probability', e.target.value)}
                  className="col-span-6 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200 md:col-span-2"
                />
                <div className="col-span-6 flex items-center justify-center rounded-xl border border-slate-200 bg-white md:col-span-2">
                  <input
                    type="color"
                    value={slice.style.backgroundColor}
                    onChange={(e) => handleSliceChange(index, 'backgroundColor', e.target.value)}
                    className="h-10 w-full cursor-pointer rounded-xl border-none bg-transparent"
                  />
                </div>
                <div className="col-span-6 flex items-center justify-center rounded-xl border border-slate-200 bg-white md:col-span-2">
                  <input
                    type="color"
                    value={slice.style.textColor}
                    onChange={(e) => handleSliceChange(index, 'textColor', e.target.value)}
                    className="h-10 w-full cursor-pointer rounded-xl border-none bg-transparent"
                  />
                </div>
                <div className="col-span-12 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => removeSlice(index)}
                    className="text-sm font-semibold text-red-500 hover:text-red-600"
                  >
                    Sil
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addSlice}
            className="mt-4 inline-flex items-center rounded-full border border-dashed border-slate-400 px-5 py-2 text-sm font-semibold text-slate-700 hover:border-slate-600"
          >
            + Yeni Dilim Ekle
          </button>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-emerald-600 px-8 py-3 text-lg font-semibold text-white shadow-lg hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Kaydediliyor...' : 'Tüm Ayarları Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
